import { useEffect, useRef, useState } from 'react';
import { BORNES_BULLE } from '@nextilt/commun';

/**
 * Le titre d'une fiche, qui se corrige sur place (ticket 10 de retours-testeurs).
 *
 * Un clic sur le titre, ou « Renommer » dans la tête de la fiche, le change en champ.
 * Entrée enregistre, Échap rend l'ancien ; quitter le champ enregistre aussi, pour ne
 * jamais perdre ce qu'on a tapé. Un titre vide ne s'enregistre pas : l'ancien reste. Ce qui
 * est tapé s'enregistre tel quel, sans majuscule ajoutée (le serveur ôte seulement les
 * blancs des bords).
 *
 * Quand le champ se referme, le focus revient au titre : le bouton qu'on avait sous les
 * doigts a été remplacé, il ne doit pas tomber sur la page.
 */
export function TitreDeFiche({
  titre,
  edition,
  onEdition,
  onRenommer,
}: {
  titre: string;
  edition: boolean;
  onEdition: (ouvert: boolean) => void;
  onRenommer: (titre: string) => void;
}) {
  const boutonTitre = useRef<HTMLButtonElement>(null);
  const etaitEnEdition = useRef(false);

  useEffect(() => {
    if (!edition && etaitEnEdition.current) boutonTitre.current?.focus();
    etaitEnEdition.current = edition;
  }, [edition]);

  if (edition) {
    return (
      <h2>
        <ChampTitre titre={titre} onRenommer={onRenommer} onFini={() => onEdition(false)} />
      </h2>
    );
  }

  return (
    <h2>
      <button
        ref={boutonTitre}
        type="button"
        className="fiche-titre"
        data-tuto="fiche-titre"
        title="Renommer"
        onClick={() => onEdition(true)}
      >
        {titre}
      </button>
    </h2>
  );
}

/**
 * Le champ, monté neuf à chaque ouverture : le brouillon part du titre du moment, tout
 * sélectionné, pour qu'on puisse le retaper d'un coup ou le corriger d'une lettre.
 */
function ChampTitre({
  titre,
  onRenommer,
  onFini,
}: {
  titre: string;
  onRenommer: (titre: string) => void;
  onFini: () => void;
}) {
  const [brouillon, setBrouillon] = useState(titre);
  /*
   * Le champ a déjà rendu la main (Entrée, Échap ou sortie) : un second `blur`, que le
   * navigateur peut envoyer quand le champ disparaît, ne réécrit ni n'annule rien.
   */
  const fini = useRef(false);

  function enregistrer() {
    if (fini.current) return;
    fini.current = true;
    const propre = brouillon.trim();
    if (propre && propre !== titre) onRenommer(propre);
    onFini();
  }

  function annuler() {
    fini.current = true;
    onFini();
  }

  return (
    <input
      className="fiche-titre-champ"
      data-tuto="fiche-titre-champ"
      value={brouillon}
      maxLength={BORNES_BULLE.titreMax}
      aria-label="Nouveau titre"
      autoFocus
      onFocus={(e) => e.currentTarget.select()}
      onChange={(e) => setBrouillon(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          enregistrer();
        } else if (e.key === 'Escape') {
          // Échap n'annule que le titre : sans ceci, il remonte jusqu'à la fenêtre, où la
          // fiche, déjà redessinée sans le champ, le prend pour elle et se ferme.
          e.stopPropagation();
          annuler();
        }
      }}
      onBlur={enregistrer}
    />
  );
}
